import { useEffect, useRef, useState } from 'react';

export interface DraftEnvelope<T> {
  values: T;
  position?: [number, number] | null;
  polygon?: [number, number][] | null;
  savedAt: number;
}

export interface UseDebouncedDraftOptions<T> {
  /** localStorage key — typically `wildfire-draft:${userId|'anon'}`. */
  storageKey: string;
  /** Current form values to persist. */
  values: T;
  /** Optional map position included in the envelope. */
  position?: [number, number] | null;
  /** Optional drawn polygon included in the envelope. */
  polygon?: [number, number][] | null;
  /** Debounce in milliseconds (default 1000). */
  debounceMs?: number;
  /** When true, do nothing (e.g. edit mode shouldn't autosave drafts). */
  disabled?: boolean;
}

/**
 * Debounced autosave of a draft envelope to localStorage. Matches the legacy
 * WildfireForm behaviour: skips the first save until first user edit so we
 * don't overwrite an existing draft on mount, then writes
 * { values, position, polygon, savedAt: Date.now() } on change with the
 * configured debounce.
 *
 * Returns `restore()` which reads the draft (or null) — caller decides when
 * to merge it into the form. Also returns `discard()` which deletes the key.
 */
export function useDebouncedDraft<T>({
  storageKey,
  values,
  position = null,
  polygon = null,
  debounceMs = 1000,
  disabled = false,
}: UseDebouncedDraftOptions<T>) {
  const skipRef = useRef(true);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (disabled) return;
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    const h = setTimeout(() => {
      try {
        const envelope: DraftEnvelope<T> = {
          values,
          position,
          polygon,
          savedAt: Date.now(),
        };
        localStorage.setItem(storageKey, JSON.stringify(envelope));
        setSavedAt(envelope.savedAt);
      } catch {
        // storage full or blocked — silently ignore
      }
    }, debounceMs);
    return () => clearTimeout(h);
  }, [storageKey, values, position, polygon, debounceMs, disabled]);

  const restore = (): DraftEnvelope<T> | null => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      return JSON.parse(raw) as DraftEnvelope<T>;
    } catch {
      return null;
    }
  };

  const discard = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  return { restore, discard, savedAt };
}
